/**
 * 発表会2027 座席割当の計算（画面から切り離した純粋な関数。seatAssignLogic.test.js でテスト）。
 *
 * 用語
 * - 席の役割(zone): S / A / B（家族を割り振る席）・staff（関係者席）・student（生徒席）・
 *   camera（カメラ席）・none（未使用）。部ごとに持つ。
 * - 割当(asg): 部ごとの Map(seatId → { fid: 抽選申込ID, t: 席種 })。
 * - 家族(family): { id, name, studio: {"1","2"}, counts: {"1": {S,A,B}, "2": {...}}, status, overdue }
 *
 * 自動割当のルール
 * - 家族の席種と同じ役割の空席にだけ置く（関係者席・生徒席・カメラ席・未使用には置かない）。
 * - 家族は通路で区切られたブロックの中の並び席にする。並びで入らなければ、1 人席が出ないように
 *   できるだけ均等に分けて（4 → 2＋2、5 → 3＋2）、隣のブロックやすぐ前後の列など座席図の上で近い席に置く。
 *   2 席・3 席のように 1 人席が避けられない家族も、予備の列に回すより近くに分ける。
 * - 1次・2次の両方に当選した家族（同じ family_key の 2 行）は 1 つの家族として、席種ごとに並びで置く
 *   （並びの前から 1次の行、続けて 2次の行の席にする）。
 * - 席種ごとに、希望数に届くまでの前の列だけを使う。届いた最後の列で余る席は、その列の両端から
 *   空けておく（空席はグレードの後ろの列にだけ残る）。それより後ろの列は入りきらないときの予備。
 * - 使う列を前から 3 列（BAND_ROWS）ずつの帯に分け、帯の中はブロックごとに、通路に当たったら
 *   次の列へ折り返しながら（蛇行）席を埋める（スタジオがブロックの中で四角くまとまる）。
 *   ブロックの順は帯ごとに左→右・右→左と折り返す。スタジオでまとめ、スタジオの順は
 *   自動割当のたびにランダムに決める（毎回同じスタジオが前に来ないように）。同じスタジオの中では
 *   席数の多い家族から置き、同じ席数ならランダム。
 * - 席を離して置く家族（f.apart。イレギュラーの希望）は並びにせず、席種ごとに 1 席ずつ、その席種の
 *   空席の中からランダムに、同じ家族の席（ほかの席種の席も含む）どうしが近すぎず縦の列もそろわない席に、
 *   ほかの家族より先に置く（使う列の中だけ。空いたまま残る席には置かない。自動割当のたびに位置が変わる）。
 *   ほかの家族が並びで入らないときは、離して置いた席を並びの端へずらして、その家族を並びで置く。
 *   それでも分かれる家族が出たら、離して置く席を選び直し（APART_TRIES 回まで）、分かれる家族が
 *   いちばん少ない置き方にする。
 * - 両方の部に席がある家族は、両部で同じ役割になっている席に同じ位置で先に置く。
 *   部によって席数が違う席種は、多いほうの席数ぶん並びを取り、少ないほうの部はその前から使う
 *   （例: 1部 S3・2部 S1 → 1部は 3 席、2部はそのうち先頭の 1 席）。
 */

export const SEAT_TYPES = ["S", "A", "B"];
export const RESERVED = ["staff", "student", "camera", "none"];
export const ZONE_KEYS = [...SEAT_TYPES, ...RESERVED];
export const PARTS = ["1", "2"];
export const ROWS1 = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
export const ROWS2 = ["2A", "2B", "2C", "2D", "2E", "2F", "2G", "2H", "2I", "2J", "2K"];
export const ROW_ORDER = [...ROWS1, ...ROWS2];
export const OTHER_STUDIOS = ["その他", "外部"];

export const isSeatType = (z) => SEAT_TYPES.includes(z);

/** API の seats（[id, floor, row, number, block, x, y, angle]）から座席の索引を作る。 */
export function buildVenue(rawSeats) {
  const seats = rawSeats.map(([id, f, r, n, b, x, y, a], i) => ({ id, i, f, r, n, b, x, y, a }));
  const byId = new Map(seats.map((s) => [s.id, s]));
  const blocks = new Map();
  for (const s of seats) {
    const k = `${s.r}|${s.b}`;
    if (!blocks.has(k)) blocks.set(k, []);
    blocks.get(k).push(s);
  }
  for (const v of blocks.values()) v.sort((a, b) => a.n - b.n);
  // 隣の席との間隔（座席図の pt）の中央値。分けて置くときの「近い」の目安
  const gaps = [...blocks.values()].flatMap((v) => v.slice(1).map((s, k) => Math.hypot(s.x - v[k].x, s.y - v[k].y)));
  gaps.sort((a, b) => a - b);
  const pitch = gaps.length ? gaps[Math.floor(gaps.length / 2)] || 1 : 1;
  return { seats, byId, blocks, pitch };
}

/** 一度も保存していない部の既定（A列＝カメラ席、ほかは未使用）。 */
export function defaultZones(venue) {
  return new Map(venue.seats.map((s) => [s.id, s.r === "A" ? "camera" : "none"]));
}

export function zonesFromApi(venue, apiZones) {
  if (!apiZones) return defaultZones(venue);
  const m = new Map();
  for (const s of venue.seats) m.set(s.id, apiZones[String(s.id)] || "none");
  return m;
}

export const seatLabel = (s) => `${s.f === 1 ? "1階" : "2階"} ${s.r}列${s.n}番`;

export function partsOf(f) {
  return PARTS.filter((p) => SEAT_TYPES.some((t) => (f.counts[p]?.[t] || 0) > 0));
}
export const isBoth = (f) => partsOf(f).length > 1;

/** 部ごと・席種ごとの希望（当選）席数の合計。 */
export function demandOf(families, part) {
  const d = { S: 0, A: 0, B: 0 };
  for (const f of families) for (const t of SEAT_TYPES) d[t] += f.counts[part]?.[t] || 0;
  return d;
}

/**
 * 希望数に合わせて S/A/B の範囲を決める（関係者席・生徒席・カメラ席はそのまま）。
 * 1階の前の列から 1 列ずつ S → A → B の順に割り当て、足りれば残りは未使用。
 * 1 階で足りなければ 2 階の前の列から続ける。
 */
export function autoZone(venue, zones, demand) {
  const next = new Map(zones);
  const order = SEAT_TYPES.filter((t) => demand[t] > 0);
  let ti = 0;
  let filled = 0;
  for (const r of ROW_ORDER) {
    const free = venue.seats.filter((s) => s.r === r && !["staff", "student", "camera"].includes(next.get(s.id)));
    if (!free.length) continue;
    while (ti < order.length && filled >= demand[order[ti]]) {
      ti += 1;
      filled = 0;
    }
    const z = ti < order.length ? order[ti] : "none";
    for (const s of free) next.set(s.id, z);
    if (z !== "none") filled += free.length;
  }
  return next;
}

/** 役割ごとの席数と、その役割がかかっている列の範囲（例 "B〜H列"）。 */
export function zoneSummary(venue, zones) {
  const cnt = Object.fromEntries(ZONE_KEYS.map((k) => [k, 0]));
  const rows = Object.fromEntries(ZONE_KEYS.map((k) => [k, []]));
  for (const s of venue.seats) {
    const z = zones.get(s.id);
    cnt[z] += 1;
    if (!rows[z].includes(s.r)) rows[z].push(s.r);
  }
  const range = {};
  for (const k of ZONE_KEYS) {
    const rs = ROW_ORDER.filter((r) => rows[k].includes(r));
    range[k] = !rs.length ? "" : rs.length === 1 ? `${rs[0]}列` : `${rs[0]}〜${rs[rs.length - 1]}列`;
  }
  const floor2 = venue.seats.filter((s) => s.f === 2 && isSeatType(zones.get(s.id))).length;
  return { count: cnt, range, floor2 };
}

// ---------------------------------------------------------------------------
// 割当の参照
// ---------------------------------------------------------------------------
export function famSeats(venue, asg, fid) {
  const out = [];
  for (const [sid, a] of asg) if (a.fid === fid) out.push({ s: venue.byId.get(sid), t: a.t });
  return out.sort((x, y) => x.s.i - y.s.i);
}

export function assignedCount(asg, fid, t) {
  let n = 0;
  for (const a of asg.values()) if (a.fid === fid && a.t === t) n += 1;
  return n;
}

/** 家族のこの部の状態（希望数・割当済み・不足・区分違い・2階）。 */
export function famStatus(venue, zones, asg, f, part) {
  const ss = famSeats(venue, asg, f.id);
  let need = 0;
  let have = 0;
  for (const t of SEAT_TYPES) {
    const n = f.counts[part]?.[t] || 0;
    need += n;
    have += Math.min(n, ss.filter((x) => x.t === t).length);
  }
  const mis = ss.filter((x) => zones.get(x.s.id) !== x.t).length;
  return { need, have, short: need - have, mis, floor2: ss.some((x) => x.s.f === 2) };
}

/**
 * 両部に席がある家族が、1部・2部で同じ席か（どちらかの部に席が無ければ null）。
 * 席種ごとに、席の少ないほうの部の席が、多いほうの部の席に含まれていれば同じとみなす。
 */
export function sameSeats(venue, asgs, f) {
  if (!isBoth(f)) return null;
  const s1 = famSeats(venue, asgs["1"], f.id);
  const s2 = famSeats(venue, asgs["2"], f.id);
  if (!s1.length || !s2.length) return null;
  for (const t of SEAT_TYPES) {
    if (!(f.counts["1"]?.[t] || 0) || !(f.counts["2"]?.[t] || 0)) continue;
    const a = s1.filter((x) => x.t === t).map((x) => x.s.id);
    const b = s2.filter((x) => x.t === t).map((x) => x.s.id);
    const [small, large] = a.length <= b.length ? [a, b] : [b, a];
    if (!small.length || !small.every((id) => large.includes(id))) return false;
  }
  return true;
}

// ---------------------------------------------------------------------------
// 自動割当
// ---------------------------------------------------------------------------
/** 自動割当で、席を埋める帯の列数（帯の中はブロックごとに通路で折り返す）。 */
export const BAND_ROWS = 3;

const BLOCKS = ["L", "C", "R"];

/** 1 列の 1 ブロックの席を（rev なら逆向きに）たどり、条件を満たす席が並びで続く区間を segs に足す。 */
function pushRuns(segs, arr, rev, pred) {
  let cur = [];
  for (const s of rev ? [...arr].reverse() : arr) {
    if (pred(s)) cur.push(s);
    else {
      if (cur.length) segs.push(cur);
      cur = [];
    }
  }
  if (cur.length) segs.push(cur);
}

const isFloor2 = (r) => r.startsWith("2");

/**
 * 席種の範囲（inBand がかかる列）のうち、need 席を置くのに使う列を決める。
 * 前の列から空席（pred）を数え、need に届いた列（last）までが使う列（main）、それより後ろは予備（reserve）。
 * last の列で余る席（spare）は、その列の両端から交互に取って空けておく（空席はグレードの後ろの列にだけ残る）。
 * 空席が足りなければ全部 main で、last・spare・reserve は無し。
 */
function planRows(venue, pred, inBand, need) {
  const rows = ROW_ORDER.filter((r) => BLOCKS.some((b) => (venue.blocks.get(`${r}|${b}`) || []).some(inBand)));
  const freeOf = (r) => BLOCKS.flatMap((b) => venue.blocks.get(`${r}|${b}`) || []).filter(pred);
  let cum = 0;
  for (let i = 0; i < rows.length; i += 1) {
    const free = freeOf(rows[i]);
    cum += free.length;
    if (cum < need) continue;
    const spare = new Set();
    for (let k = 0; k < cum - need; k += 1) spare.add((k % 2 === 0 ? free[k / 2] : free[free.length - 1 - (k - 1) / 2]).id);
    return { main: rows.slice(0, i + 1), last: rows[i], spare, reserve: rows.slice(i + 1) };
  }
  return { main: rows, last: null, spare: new Set(), reserve: [] };
}

/**
 * 割当の順に、条件を満たす席が並びで続く区間（ブロックの中だけ）を作る。
 * planRows の main の列を前から BAND_ROWS 列ずつの帯に分け（階が変われば帯も切る）、
 * 帯の中はブロックごとに、列ごとに左右を折り返しながら（通路で折り返す蛇行）前から埋める。
 * ブロックの順は帯ごとに左→右・右→左と折り返す（帯をまたいだスタジオが同じブロックに続くように）。
 * 返り値: { used: 最後の列の余る席を除いた区間, main: 余る席も含めた区間（同じ順）, reserve: 予備の列の区間 }
 */
function segments(venue, pred, inBand, need) {
  const plan = planRows(venue, pred, inBand, need);
  const bands = [];
  for (const r of plan.main) {
    const last = bands[bands.length - 1];
    if (last && last.floor2 === isFloor2(r) && last.rows.length < BAND_ROWS) last.rows.push(r);
    else bands.push({ floor2: isFloor2(r), rows: [r] });
  }
  const walk = (p) => {
    const runs = [];
    bands.forEach((band, bi) => {
      for (const b of bi % 2 === 0 ? BLOCKS : [...BLOCKS].reverse()) {
        band.rows.forEach((r, k) => {
          const arr = venue.blocks.get(`${r}|${b}`);
          if (arr) pushRuns(runs, arr, k % 2 === 1, p);
        });
      }
    });
    return runs;
  };
  const reserve = [];
  for (const r of plan.reserve) {
    for (const b of BLOCKS) {
      const arr = venue.blocks.get(`${r}|${b}`);
      if (arr) pushRuns(reserve, arr, false, pred);
    }
  }
  return { used: walk((s) => pred(s) && !plan.spare.has(s.id)), main: walk(pred), reserve };
}

const seatDist = (a, b) => (a.f !== b.f ? Infinity : Math.hypot(a.x - b.x, a.y - b.y));
const groupDist = (as, bs) => Math.min(...as.flatMap((a) => bs.map((b) => seatDist(a, b))));

/**
 * n 席の家族の分け方の候補（大きい塊から）。1 人席の少ない順 → 塊の少ない順 → 塊の大きさがそろっている順。
 * 例: 4 → [2,2] [3,1] [2,1,1] …、3 → [2,1] [1,1,1]、2 → [1,1]
 */
export function splitPatterns(n) {
  const out = [];
  const rec = (left, max, cur) => {
    if (!left) {
      if (cur.length > 1) out.push(cur);
      return;
    }
    for (let k = Math.min(left, max); k >= 1; k -= 1) rec(left - k, k, [...cur, k]);
  };
  if (n <= 12) rec(n, n - 1, []);
  else for (let k = 2; k <= n; k += 1) out.push(Array.from({ length: k }, (_, i) => Math.floor(n / k) + (i < n % k ? 1 : 0)));
  const ones = (p) => p.filter((x) => x === 1).length;
  return out.sort((a, b) => ones(a) - ones(b) || a.length - b.length || a[0] - a[a.length - 1] - (b[0] - b[b.length - 1]));
}

/** runs の中の、空いている k 席の並び（区間の前から）。 */
function windows(runs, k, isFree) {
  const out = [];
  for (const run of runs) {
    let len = 0;
    run.forEach((s, i) => {
      len = isFree(s) ? len + 1 : 0;
      if (len >= k) out.push(run.slice(i - k + 1, i + 1));
    });
  }
  return out;
}

/**
 * 並びで入らない家族を分けて、近くに置く案を作る（まだ席は取らない）。
 * 1 つ目の塊は runs の順で最初に入るところ（スタジオの続き）に、残りの塊は 1 つ目の塊に
 * 座席図の上でいちばん近い空きに置く。分け方は splitPatterns の順に試し、近い（near 以内）案が
 * あれば最初のものを、なければいちばん近い案を使う。返り値: 席の配列の配列 / null
 */
function splitPlan(n, runs, near, isFree) {
  // anchor から残りの塊を近い順に取る。返り値: { pieces, spread } / null
  const build = (pat, anchor) => {
    const planned = new Set(anchor.map((s) => s.id));
    const free = (s) => isFree(s) && !planned.has(s.id);
    const pieces = [anchor];
    let spread = 0;
    for (const k of pat.slice(1)) {
      let pick = null;
      let pickD = Infinity;
      for (const w of windows(runs, k, free)) {
        const d = groupDist(w, anchor);
        if (d < pickD) {
          pick = w;
          pickD = d;
        }
      }
      if (!pick) return null;
      pick.forEach((s) => planned.add(s.id));
      pieces.push(pick);
      spread = Math.max(spread, pickD);
    }
    return { pieces, spread };
  };
  let best = null;
  for (const pat of splitPatterns(n)) {
    // 1 つ目の塊は、入る最初の区間の中で、残りの塊がいちばん近くなる位置に
    const first = runs.find((run) => windows([run], pat[0], isFree).length);
    if (!first) continue;
    let cur = null;
    for (const anchor of windows([first], pat[0], isFree)) {
      const r = build(pat, anchor);
      if (r && (!cur || r.spread < cur.spread)) cur = r;
    }
    if (!cur) continue;
    if (cur.spread <= near) return cur.pieces;
    if (!best || cur.spread < best.spread) best = cur;
  }
  return best ? best.pieces : null;
}

/**
 * 離して置いた席を 1 つずらせば、n 席の並びが入るところ（ブロックの中の n+1 席の並びで、
 * 真ん中に離して置いた席が 1 つ、ほかは runs の空席）。離して置いた席を並びの端（to）へずらし、
 * 残りの n 席（seats）を使う。runs の順でいちばん前のもの。返り値: { from, to, seats } / null
 */
function shiftFit(venue, n, runs, isFree, movable) {
  const order = new Map(runs.flat().map((s, i) => [s.id, i]));
  let best = null;
  let bestK = Infinity;
  for (const arr of venue.blocks.values()) {
    for (let i = 0; i + n < arr.length; i += 1) {
      const win = arr.slice(i, i + n + 1);
      const busy = win.filter((s) => !order.has(s.id) || !isFree(s));
      if (busy.length !== 1) continue;
      const from = busy[0];
      const j = win.indexOf(from);
      if (j === 0 || j === n || !movable.can(from)) continue;
      const k = Math.min(...win.filter((s) => s !== from).map((s) => order.get(s.id)));
      if (k >= bestK) continue;
      const to = [win[0], win[n]].find((x) => movable.fits(from, x));
      if (!to) continue;
      best = { from, to, seats: win.filter((s) => s !== to) };
      bestK = k;
    }
  }
  return best;
}

/**
 * groups を区間に置く。asgList は書き込む部の割当（両部同時に置くときは 2 つ）。
 * 並びは g.n 席取り、g.owners[k]（部ごとの、並びの前から座る申込 ID の列）の数だけ各部で使う。
 * 置く場所の順: 使う列の並び → 最後の列の余る席も使った並び → 使う列で、離して置いた席をずらして並び
 * （movable があるとき）→ 使う列で分けて近くに → 予備の列の並び → どこでも分けて近くに。
 * 返り値: { left: 置けなかった groups, split: 分けて置いた groups の数 }
 */
function placeGroups(venue, groups, segs, asgList, movable = null) {
  const left = [];
  let split = 0;
  const near = venue.pitch * 3.5;
  const taken = new Set();
  const isFree = (s) => !taken.has(s.id);
  // スタジオが替わったら、そのスタジオは今の続き（最初の空席のある区間）から。届かなければ前に戻る
  const rotate = (runs, at) => {
    const i = at ? runs.findIndex((run) => run.includes(at)) : -1;
    return i < 0 ? runs : [...runs.slice(i), ...runs.slice(0, i)];
  };
  let studio = null;
  let at = null;
  for (const g of groups) {
    if (g.studio !== studio) {
      studio = g.studio;
      at = segs.used.flat().find(isFree) || null;
    }
    const used = rotate(segs.used, at);
    const main = rotate(segs.main, at);
    const whole = (runs) => {
      const w = windows(runs, g.n, isFree)[0];
      return w ? [w] : null;
    };
    let pieces = whole(used) || whole(main);
    if (!pieces && g.n > 1 && movable) {
      const sh = shiftFit(venue, g.n, used, isFree, movable);
      if (sh) {
        movable.move(sh.from, sh.to);
        taken.add(sh.to.id);
        pieces = [sh.seats];
      }
    }
    if (!pieces && g.n > 1) pieces = splitPlan(g.n, main, near, isFree);
    if (!pieces) pieces = whole(segs.reserve);
    if (!pieces && g.n > 1) pieces = splitPlan(g.n, [...main, ...segs.reserve], Infinity, isFree);
    if (!pieces) {
      left.push(g);
      continue;
    }
    if (pieces.length > 1) split += 1;
    pieces.flat().forEach((s, m) => {
      taken.add(s.id);
      asgList.forEach((asg, j) => {
        if (m < g.owners[j].length) asg.set(s.id, { fid: g.owners[j][m], t: g.t });
      });
    });
  }
  return { left, split };
}

/** 一覧・色分けのスタジオの順（固定。外部・その他は最後）。 */
export function studioOrder(families, part) {
  const set = new Set(families.filter((f) => partsOf(f).includes(part)).map((f) => f.studio[part]));
  return [...set].sort(
    (a, b) => OTHER_STUDIOS.indexOf(a) - OTHER_STUDIOS.indexOf(b) || a.localeCompare(b, "ja"),
  );
}

/** 混ぜた新しい配列を返す（Fisher–Yates。元の配列は変えない）。rand はテストで差し替える。 */
export function shuffle(arr, rand = Math.random) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/** 自動割当のスタジオの順。両部のスタジオ（外部・その他も含む）を毎回ランダムに並べる。 */
export function randomStudioOrder(families, rand = Math.random) {
  const set = new Set();
  for (const f of families) for (const p of partsOf(f)) set.add(f.studio[p]);
  return shuffle([...set], rand);
}

function sortGroups(groups, order) {
  // 同じスタジオ・同じ席数の並びは、渡された順（ランダムに混ぜた家族の順）のまま
  return groups.sort((a, b) => order.indexOf(a.studio) - order.indexOf(b.studio) || b.n - a.n);
}

const freeIn = (zones, asg, s, z) => zones.get(s.id) === z && !asg.has(s.id);

/**
 * 同じ family_key の行（1次・2次）を 1 つの家族にまとめる。
 * { key, ids: [申込 ID（1次→2次）], rows, studio, counts: 行の合計 }
 */
export function familyUnits(families) {
  const units = new Map();
  const rows = [...families].sort((a, b) => (a.round || 1) - (b.round || 1));
  for (const f of rows) {
    const key = f.family_key || `id:${f.id}`;
    if (!units.has(key)) {
      units.set(key, {
        key,
        ids: [],
        rows: [],
        studio: f.studio,
        apart: false,
        counts: Object.fromEntries(PARTS.map((p) => [p, { S: 0, A: 0, B: 0 }])),
      });
    }
    const u = units.get(key);
    u.apart = u.apart || !!f.apart;
    u.ids.push(f.id);
    u.rows.push(f);
    for (const p of PARTS) for (const t of SEAT_TYPES) u.counts[p][t] += f.counts[p]?.[t] || 0;
  }
  return [...units.values()];
}

/** まとめた家族のこの部・この席種で、まだ席の無い分（1次の行→2次の行の順の申込 ID の列）。 */
function ownersOf(u, asg, part, t) {
  const out = [];
  for (const f of u.rows) {
    const n = (f.counts[part]?.[t] || 0) - assignedCount(asg, f.id, t);
    for (let i = 0; i < n; i += 1) out.push(f.id);
  }
  return out;
}

function unitSeats(venue, asg, u, t) {
  const ids = new Set(u.ids);
  const out = [];
  for (const [sid, a] of asg) if (ids.has(a.fid) && a.t === t) out.push(venue.byId.get(sid));
  return out.sort((x, y) => x.i - y.i);
}

/**
 * もう一方の部と同じ席が空いていて役割も同じなら、その席をそのまま使う（両部の家族）。
 * もう一方の部の席のほうが多ければ、その前から必要な数だけ使う。
 * 返り値: 置けなかった席種（もう一方の部の席が足りない・空いていないなど）
 */
export function copyFromOther(venue, zonesAll, asgs, u, part) {
  const other = part === "1" ? "2" : "1";
  const failed = [];
  for (const t of SEAT_TYPES) {
    const need = u.counts[part]?.[t] || 0;
    if (!need || unitSeats(venue, asgs[part], u, t).length > 0) continue;
    const owners = ownersOf(u, asgs[part], part, t);
    const src = unitSeats(venue, asgs[other], u, t).slice(0, need);
    if (src.length === need && src.every((s) => freeIn(zonesAll[part], asgs[part], s, t))) {
      src.forEach((s, m) => asgs[part].set(s.id, { fid: owners[m], t }));
    } else failed.push(t);
  }
  return failed;
}

/**
 * 離して置く席どうしの条件。前から順に試し、置ける席が無ければ次の（ゆるい）条件にする。
 * d: 座席図の上でこの席数ぶん以上離す。col: 縦の列（x がほぼ同じ＝真後ろ・真前）にしない。
 */
const APART_RULES = [
  { d: 4, col: true },
  { d: 2, col: true },
  { d: 2, col: false },
  { d: 0, col: false },
];

/** s が、同じ家族の席 others から rule の条件で離れているか（階が違えば離れているとみなす）。 */
function apartFits(venue, others, s, rule) {
  return others.every(
    (p) =>
      p.f !== s.f ||
      (Math.hypot(p.x - s.x, p.y - s.y) >= venue.pitch * rule.d && (!rule.col || Math.abs(p.x - s.x) >= venue.pitch)),
  );
}

/** 離して置く席を選び直す回数の上限（ほかの家族が分かれないように）。 */
export const APART_TRIES = 20;

/**
 * 席を離して置く家族の席を、その席種の空席からランダムに 1 席ずつ置く。
 * 同じ家族のすでにある席（ほかの席種の席も含む。mine）と近すぎず縦の列もそろわない席（APART_RULES）の中から選ぶ。
 * いちばん遠い席を選ぶ方式だと、毎回会場の四隅・端の列にそろってしまっていた。
 * ok（使う列のうち、最後の列の余る席を除いた席）の中だけで選ぶ。余る席や予備の列に置くと
 * 周りに誰もいない 1 人席になる。
 */
function placeApart(venue, zones, asg, owners, t, ok, rand, mine) {
  const free = venue.seats.filter((s) => ok(s) && freeIn(zones, asg, s, t));
  const picked = [];
  while (picked.length < owners.length) {
    const near = [...mine, ...picked];
    const cands = (rule) => free.filter((s) => !picked.includes(s) && apartFits(venue, near, s, rule));
    const rule = APART_RULES.find((r) => cands(r).length);
    if (!rule) break;
    const c = cands(rule);
    picked.push(c[Math.floor(rand() * c.length)]);
  }
  picked.forEach((s, m) => asg.set(s.id, { fid: owners[m], t }));
}

/**
 * 1 つの部の、まだ席の無い家族を置く（今の割当は残す）。
 * order はスタジオの順（省略すると毎回ランダム）。
 */
export function autoAssignPart(venue, zonesAll, asgs, families, part, order, rand = Math.random) {
  const zones = zonesAll[part];
  const asg = asgs[part];
  const units = shuffle(familyUnits(families), rand).filter((u) => partsOf(u).includes(part));
  for (const u of units) if (isBoth(u)) copyFromOther(venue, zonesAll, asgs, u, part);
  const studios = order || randomStudioOrder(families, rand);
  const other = asgs[part === "1" ? "2" : "1"];
  const apartUnits = units.filter((u) => u.apart);
  const apartUnit = new Map(apartUnits.flatMap((u) => u.ids.map((id) => [id, u])));
  // 家族のこの部の席（席種を問わない。except の席は除く）
  const seatsOfUnit = (u, except = null) => {
    const ids = new Set(u.ids);
    return [...asg].filter(([sid, a]) => sid !== except && ids.has(a.fid)).map(([sid]) => venue.byId.get(sid));
  };
  // 離して置いた席のうち、ずらしてよい席（もう一方の部と同じ席にしている両部の家族の席はずらさない）
  const movable = (t) => ({
    can: (s) => {
      const a = asg.get(s.id);
      return !!a && a.t === t && apartUnit.has(a.fid) && other.get(s.id)?.fid !== a.fid;
    },
    // ずらした先も、同じ家族のほかの席と近すぎず縦の列がそろわないこと
    fits: (from, to) => apartFits(venue, seatsOfUnit(apartUnit.get(asg.get(from.id).fid), from.id), to, APART_RULES[1]),
    move: (from, to) => {
      asg.set(to.id, asg.get(from.id));
      asg.delete(from.id);
    },
  });
  for (const t of SEAT_TYPES) {
    const pred = (s) => freeIn(zones, asg, s, t);
    const inBand = (s) => zones.get(s.id) === t;
    const need = units.reduce((n, u) => n + ownersOf(u, asg, part, t).length, 0);
    const plan = planRows(venue, pred, inBand, need);
    // 離して置く家族は使う列の中で、最後の列の余る席（空いたまま残る席）を除いたところに
    const apartOk = (x) => plan.main.includes(x.r) && !plan.spare.has(x.id);
    const groups = [];
    for (const u of units.filter((x) => !x.apart)) {
      const owners = ownersOf(u, asg, part, t);
      if (owners.length) groups.push({ t, n: owners.length, owners: [owners], studio: u.studio[part] });
    }
    sortGroups(groups, studios);
    const apartOwners = apartUnits.map((u) => [u, ownersOf(u, asg, part, t)]).filter(([, o]) => o.length);
    // 離して置く家族がいれば、その席を選び直して、分かれる家族（置けない家族はもっと悪い）がいちばん少ない置き方に
    const base = new Map(asg);
    let best = null;
    for (let k = 0; k < (apartOwners.length ? APART_TRIES : 1); k += 1) {
      if (k) {
        asg.clear();
        base.forEach((a, sid) => asg.set(sid, a));
      }
      for (const [u, owners] of apartOwners) placeApart(venue, zones, asg, owners, t, apartOk, rand, seatsOfUnit(u));
      const rest = groups.reduce((n, g) => n + g.n, 0);
      const r = placeGroups(venue, groups, segments(venue, pred, inBand, rest), [asg], movable(t));
      const score = r.left.length * 10000 + r.split;
      if (!best || score < best.score) best = { score, asg: new Map(asg) };
      if (!score) break;
    }
    asg.clear();
    best.asg.forEach((a, sid) => asg.set(sid, a));
  }
}

/**
 * 両部とも割り当て直す。先に両部の家族を両部で同じ役割の席へ同じ位置で置く。
 * スタジオの順は呼ぶたびにランダム（両部で同じ順）。
 */
export function autoAssignAll(venue, zonesAll, families, rand = Math.random) {
  const asgs = { 1: new Map(), 2: new Map() };
  const units = shuffle(familyUnits(families), rand);
  const order = randomStudioOrder(families, rand);
  for (const t of SEAT_TYPES) {
    const groups = units
      .filter((u) => !u.apart && isBoth(u) && (u.counts["1"]?.[t] || 0) > 0 && (u.counts["2"]?.[t] || 0) > 0)
      .map((u) => {
        const owners = [ownersOf(u, asgs["1"], "1", t), ownersOf(u, asgs["2"], "2", t)];
        return { t, n: Math.max(owners[0].length, owners[1].length), owners, studio: u.studio["1"] };
      });
    placeGroups(
      venue,
      sortGroups(groups, order),
      segments(
        venue,
        (s) => freeIn(zonesAll["1"], asgs["1"], s, t) && freeIn(zonesAll["2"], asgs["2"], s, t),
        (s) => zonesAll["1"].get(s.id) === t,
        groups.reduce((n, g) => n + g.n, 0),
      ),
      [asgs["1"], asgs["2"]],
    );
  }
  autoAssignPart(venue, zonesAll, asgs, families, "1", order, rand);
  autoAssignPart(venue, zonesAll, asgs, families, "2", order, rand);
  return { 1: asgs["1"], 2: asgs["2"] };
}

// ---------------------------------------------------------------------------
// 手での配置
// ---------------------------------------------------------------------------
/**
 * 家族を、クリックした席から並びで置く（すでに置いていれば移動）。
 * 席種は、クリックした席の役割を家族が希望していればその席種、なければ不足している席種。
 * 返り値: { ok, t, n }（ok=false は並びで空いていない）
 */
export function placeAt(venue, zones, asg, f, part, seat) {
  const types = SEAT_TYPES.filter((t) => (f.counts[part]?.[t] || 0) > 0);
  if (!types.length) return { ok: false, t: null, n: 0 };
  let t = types.includes(zones.get(seat.id)) ? zones.get(seat.id) : null;
  if (!t) t = types.find((x) => assignedCount(asg, f.id, x) < f.counts[part][x]) || types[0];
  const n = f.counts[part][t];
  const arr = venue.blocks.get(`${seat.r}|${seat.b}`);
  const idx = arr.indexOf(seat);
  const mine = (a) => a && a.fid === f.id && a.t === t;
  for (let d = 0; d < n; d += 1) {
    const j = idx - d;
    if (j < 0 || j + n > arr.length) continue;
    const win = arr.slice(j, j + n);
    if (win.every((x) => isSeatType(zones.get(x.id)) && (!asg.has(x.id) || mine(asg.get(x.id))))) {
      for (const [sid, a] of [...asg]) if (mine(a)) asg.delete(sid);
      for (const x of win) asg.set(x.id, { fid: f.id, t });
      return { ok: true, t, n };
    }
  }
  return { ok: false, t, n };
}

/**
 * 1 席だけ置く・外す（Shift+クリック。家族の席を離して置きたいとき）。
 * その家族の席なら、その 1 席を外す。空いている席なら、まだ足りない席種の席を 1 席置く
 * （クリックした席の役割の席種が足りていればその席種）。
 * 返り値: { ok, action: "add" | "remove", t, reason: "taken" | "full" | "blocked" }
 */
export function placeOne(zones, asg, f, part, seat) {
  const cur = asg.get(seat.id);
  if (cur) {
    if (cur.fid !== f.id) return { ok: false, reason: "taken" };
    asg.delete(seat.id);
    return { ok: true, action: "remove", t: cur.t };
  }
  const z = zones.get(seat.id);
  if (!isSeatType(z)) return { ok: false, reason: "blocked" };
  const short = SEAT_TYPES.filter((t) => assignedCount(asg, f.id, t) < (f.counts[part]?.[t] || 0));
  if (!short.length) return { ok: false, reason: "full" };
  const t = short.includes(z) ? z : short[0];
  asg.set(seat.id, { fid: f.id, t });
  return { ok: true, action: "add", t };
}

/** 席の役割を家族を置けない役割に変えたとき、その席に座っていた家族のその席種の席をすべて外す。 */
export function releaseBlocked(zones, asg) {
  const released = new Set();
  for (const [sid, a] of asg) if (!isSeatType(zones.get(sid))) released.add(`${a.fid}|${a.t}`);
  if (!released.size) return [];
  for (const [sid, a] of [...asg]) if (released.has(`${a.fid}|${a.t}`)) asg.delete(sid);
  return [...released].map((k) => Number(k.split("|")[0]));
}

/** 保存用（API の形）。 */
export function toSavePayload(venue, zones, asg) {
  const z = {};
  for (const s of venue.seats) z[s.id] = zones.get(s.id);
  const assignments = [...asg].map(([sid, a]) => ({ seat_id: sid, application_id: a.fid, seat_type: a.t }));
  return { zones: z, assignments };
}
